import React from 'react';

import { useMy } from '@/hooks';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { AllIntegrations } from '@/hooks/useIntegration';
import {
  CustomerUsersTable,
  PraetorianUsersTable,
} from '@/sections/components/AuthorizedUsersTables';
import { useAuth } from '@/state/auth';
import { useMergeStatus } from '@/utils/api';
import { getSSODomains } from '@/utils/userDisplay.util';

export const Users: React.FC<{
  customerEmail?: string;
  variant?: 'default' | 'compact';
}> = ({ customerEmail, variant = 'default' }) => {
  const { friend, me: primaryEmail, userType } = useAuth();

  const {
    data: accounts,
    status: accountsStatus,
    error,
  } = useMy(
    { resource: 'account' },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  const { data: accessGenericSearch } = useGenericSearch(
    {
      query: '#access',
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  const access = accessGenericSearch?.accesss || [];

  // Build Map for O(1) lookups - React Compiler handles memoization
  const accessByEmail = new Map<string, string | undefined>(
    access.filter(item => item.email).map(item => [item.email, item.updated])
  );

  const users = [
    {
      username: friend || primaryEmail,
      member: friend || primaryEmail,
      name: friend || primaryEmail,
      updated: '',
      config: {},
      key: 'self',
    },
    ...(accounts || [])
      .filter(
        account =>
          !(account.member in AllIntegrations) &&
          account.member !== account.username &&
          account.member.includes('@')
      )
      .map(account => ({
        ...account,
        access: accessByEmail.get(account.member),
      })),
  ];

  // Extract configured SSO domains for auth method detection
  // React Compiler handles memoization automatically
  const ssoDomains = getSSODomains(accounts || []);

  const mergedStatus = useMergeStatus(accountsStatus);

  return (
    <div className="flex flex-col gap-4">
      {userType === 'praetorian' && (
        <PraetorianUsersTable
          users={users}
          error={error}
          status={mergedStatus}
          customerEmail={customerEmail}
          showHeader={true}
          ssoDomains={ssoDomains}
          variant={variant}
        />
      )}
      <CustomerUsersTable
        users={users}
        error={error}
        status={mergedStatus}
        customerEmail={customerEmail}
        showHeader={true}
        ssoDomains={ssoDomains}
        variant={variant}
      />
    </div>
  );
};
